import { useContext, useState, useRef, useEffect, useMemo } from "react";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { useI18n } from "i18n";
import classNames from "classnames";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import "./styles.scss";
import { useSelector } from "react-redux";
import { selectIsMobile } from "modules/deviceInfo";
import {
  selectDisableFreshchatWidget,
  selectEnableAgencyWhiteLabelling,
  selectIsCompanySettingsLoaded,
} from "modules/companySettings/redux/selectors";
import { UpdatePrompt } from "modules/version";
import {
  selectCompanyId,
  selectIsLoading as selectIsCompanyLoading,
} from "modules/company/redux/selectors";
import { selectCustomDomainCompanyID } from "modules/settings/redux/selectors";

const HEADWAY_LOADING_CHECK_INTERVAL = 500;

const WIDGET_CONTAINER_ID = "headway-container";

const wrapperClassName = "headway-container-wrapper";

interface Props {
  isHeadwayLoaded: boolean;
  onClick: () => void;
  wrapWithUpdatePrompt?: boolean;
}

export const ChangelogButtonComponent = ({
  isHeadwayLoaded,
  onClick,
  wrapWithUpdatePrompt,
}: Props) => {
  const { t } = useI18n(["menu"]);

  const component = (
    <Button
      variant="tertiary"
      data-testid="changelog-test"
      id={WIDGET_CONTAINER_ID}
      className={classNames("headway-container", {
        "headway-container-loaded": isHeadwayLoaded,
      })}
      onClick={onClick}
      onKeyPress={onClick}
      tabIndex={0}
      contentWrapperClassName={wrapperClassName}
    >
      <Typography variant="h5">{t("menu:whats.new")}</Typography>
    </Button>
  );

  if (wrapWithUpdatePrompt) {
    return <UpdatePrompt placement="bottom">{component}</UpdatePrompt>;
  }

  return component;
};

const ChangelogButton = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { track } = useContext(TRACKING_CONTEXT);
  const companyId = useSelector(selectCompanyId);
  const isCompanyLoading = useSelector(selectIsCompanyLoading);
  const customDomainCompanyID = useSelector(selectCustomDomainCompanyID);
  const isMobileView = useSelector(selectIsMobile);
  const isCompanySettingsLoaded = useSelector(selectIsCompanySettingsLoaded);
  const disableFreshchatWidget = useSelector(selectDisableFreshchatWidget);
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const timeoutId = useRef(0);

  const wrapWithUpdatePrompt = useMemo(() => {
    if (!isCompanySettingsLoaded) {
      return true;
    }

    return enableAgencyWhiteLabelling && disableFreshchatWidget;
  }, [
    isCompanySettingsLoaded,
    disableFreshchatWidget,
    enableAgencyWhiteLabelling,
  ]);

  const isHeadwayEnabled = useMemo(() => {
    if (isMobileView) {
      return false;
    }

    if (isCompanyLoading) {
      return false;
    }

    return Boolean(companyId || !customDomainCompanyID);
  }, [isMobileView, isCompanyLoading, companyId, customDomainCompanyID]);

  useEffect(() => {
    if (!isHeadwayEnabled) return;

    const HEADWAY_CONFIG = {
      selector: `#${WIDGET_CONTAINER_ID}`,
      account: process.env.REACT_APP_HEADWAY_ACCOUNT || "",
      trigger: `#${WIDGET_CONTAINER_ID}`,
      callbacks: {
        onWidgetReady() {
          setIsLoaded(true);
          track(Events.CHANGELOG_INITIALIZATION_COMPLETE);
        },
        onShowWidget() {
          track(Events.CHANGELOG_SHOW);
        },
        onShowDetails(e: {
          title: string;
          position: number;
          category: string;
        }) {
          track(Events.CHANGELOG_SHOW_DETAILS, {
            title: e.title,
            position: e.position,
            category: e.category,
          });
        },
        onReadMore() {
          track(Events.CHANGELOG_READ_MORE);
        },
        onHideWidget() {
          track(Events.CHANGELOG_HIDE);
        },
      },
    };

    const initHeadwayWhenDomReadyAndScriptLoaded = () => {
      if (window.Headway && document.querySelector(HEADWAY_CONFIG.selector)) {
        window.Headway.init(HEADWAY_CONFIG);
      } else {
        timeoutId.current = window.setTimeout(
          initHeadwayWhenDomReadyAndScriptLoaded,
          HEADWAY_LOADING_CHECK_INTERVAL,
        );
      }
    };

    timeoutId.current = window.setTimeout(
      initHeadwayWhenDomReadyAndScriptLoaded,
      HEADWAY_LOADING_CHECK_INTERVAL,
    );

    // eslint-disable-next-line consistent-return
    return () => {
      window.clearTimeout(timeoutId.current);
    };
  }, [isHeadwayEnabled, track]);

  const showChangelogWidget = () => {
    window.Headway.show();
  };

  if (!isHeadwayEnabled) return null;

  return (
    <ChangelogButtonComponent
      isHeadwayLoaded={isLoaded}
      onClick={showChangelogWidget}
      wrapWithUpdatePrompt={wrapWithUpdatePrompt}
    />
  );
};

export default ChangelogButton;
